import { Group, useMantineTheme } from '@mantine/core';
import { useTimeout } from '@mantine/hooks';
import {
  MantineReactTable,
  MRT_ColumnFilterFnsState,
  MRT_ColumnFiltersState,
  MRT_ColumnOrderState,
  MRT_ColumnSizingState,
  MRT_DensityState,
  MRT_ExpandedState,
  MRT_PaginationState,
  MRT_Row,
  MRT_RowData,
  MRT_RowSelectionState,
  MRT_ShowHideColumnsButton,
  MRT_SortingState,
  MRT_TableInstance,
  MRT_TableOptions,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton,
  MRT_VisibilityState,
  useMantineReactTable,
  type MRT_ColumnDef,
} from 'mantine-react-table';
import { IconRefresh } from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';
import { BasicActionIcon } from 'src/design/atoms/BasicActionIcon';
import { SecondaryButton } from 'src/design/atoms/SecondaryButton';
import { ExportCsvToolbarAction } from 'src/design/organisms/ExportCsvToolbarAction';
import classes from './table.module.css';

export interface IUseTableStateProps<T extends MRT_RowData> {
  identifier: string;
  columns: MRT_ColumnDef<T>[];
  defaultColumnFilters?: MRT_ColumnFiltersState;
  defaultColumnFilterFns?: MRT_ColumnFilterFnsState;
  defaultColumnVisibility?: MRT_VisibilityState;
  defaultColumnSizing?: MRT_ColumnSizingState;
  defaultColumnOrder?: MRT_ColumnOrderState;
  defaultDensity?: MRT_DensityState;
  defaultGlobalFilter?: string;
  defaultSorting?: MRT_SortingState;
  defaultPagination?: MRT_PaginationState;
  defaultShowGlobalFilter?: boolean;
  defaultShowColumnFilters?: boolean;
}

// Defaults every column's filter mode to "contains" unless the caller supplied one.
const buildDefaultColumnFilterFns = <T extends MRT_RowData>(
  columns: MRT_ColumnDef<T>[],
  provided?: MRT_ColumnFilterFnsState,
): MRT_ColumnFilterFnsState =>
  provided ??
  (Object.fromEntries(
    columns.map((column) => [column.accessorKey ?? column.id, 'contains']),
  ) as MRT_ColumnFilterFnsState);

/** @registry Manages DataTable state (filters, sorting, pagination, density, column sizing/visibility) with localStorage persistence keyed by `identifier`; pairs with useTable. */
export const useTableState = <T extends MRT_RowData>({
  identifier,
  columns,
  defaultColumnFilters,
  defaultColumnFilterFns,
  defaultColumnVisibility,
  defaultColumnSizing,
  defaultColumnOrder,
  defaultDensity,
  defaultGlobalFilter,
  defaultSorting,
  defaultPagination,
  defaultShowColumnFilters,
  defaultShowGlobalFilter,
}: IUseTableStateProps<T>) => {
  const [isFirstRender, setIsFirstRender] = useState(true);
  const [writeBlock, setWriteBlock] = useState(true);
  useTimeout(() => setWriteBlock(false), 2000, {
    autoInvoke: true,
  });

  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    defaultColumnFilters ?? [],
  );
  const [columnFilterFns, setColumnFilterFns] =
    useState<MRT_ColumnFilterFnsState>(
      buildDefaultColumnFilterFns(columns, defaultColumnFilterFns),
    );

  const [columnVisibility, setColumnVisibility] = useState<MRT_VisibilityState>(
    defaultColumnVisibility ?? {},
  );

  const [columnSizing, setColumnSizing] = useState<MRT_ColumnSizingState>(
    defaultColumnSizing ?? {},
  );

  const [density, setDensity] = useState<MRT_DensityState>(
    defaultDensity ?? 'xs',
  );

  const [globalFilter, setGlobalFilter] = useState(defaultGlobalFilter ?? '');
  const [sorting, setSorting] = useState<MRT_SortingState>(
    defaultSorting ?? [],
  );
  const [pagination, setPagination] = useState<MRT_PaginationState>(
    defaultPagination ?? {
      pageIndex: 0,
      pageSize: 10,
    },
  );

  const [showGlobalFilter, setShowGlobalFilter] = useState(
    defaultShowGlobalFilter ?? false,
  );
  const [showColumnFilters, setShowColumnFilters] = useState(
    defaultShowColumnFilters ?? false,
  );

  const start = pagination.pageIndex * pagination.pageSize;
  const length = start + pagination.pageSize;
  const filters = columnFilters.map((value) => ({
    id: value.id,
    values: [].concat(value.value as any),
  }));
  const filterFns = Object.entries(columnFilterFns)
    .map((value) => ({
      id: value[0],
      value: value[1].toString(),
    }))
    .filter((i) => i.id !== 'undefined');

  // Load state from local storage
  useEffect(() => {
    const savedState = localStorage.getItem(identifier + '_mrt_state');

    const parsedState = JSON.parse(savedState ?? '{}');

    parsedState.columnFilters && setColumnFilters(parsedState.columnFilters);
    parsedState.columnFilterFns &&
      setColumnFilterFns(parsedState.columnFilterFns);
    parsedState.columnVisibility &&
      setColumnVisibility(parsedState.columnVisibility);
    parsedState.columnSizing && setColumnSizing(parsedState.columnSizing);
    parsedState.density && setDensity(parsedState.density);
    parsedState.globalFilter && setGlobalFilter(parsedState.globalFilter);
    parsedState.sorting && setSorting(parsedState.sorting);
    parsedState.pagination && setPagination(parsedState.pagination);
    parsedState.showGlobalFilter &&
      setShowGlobalFilter(parsedState.showGlobalFilter);
    parsedState.showColumnFilters &&
      setShowColumnFilters(parsedState.showColumnFilters);

    setIsFirstRender(false);
  }, [identifier]);

  // Save state to local storage
  useEffect(() => {
    if (isFirstRender || writeBlock) return;

    const stateToSave = {
      columnFilters,
      columnFilterFns,
      columnVisibility,
      columnSizing,
      density,
      globalFilter,
      sorting,
      pagination,
      showGlobalFilter,
      showColumnFilters,
    };

    localStorage.setItem(
      identifier + '_mrt_state',
      JSON.stringify(stateToSave),
    );
  }, [
    columnFilters,
    columnFilterFns,
    columnVisibility,
    columnSizing,
    density,
    globalFilter,
    sorting,
    pagination,
    showGlobalFilter,
    showColumnFilters,
    identifier,
  ]);

  // All fields here are fed into MRT's controlled `state` prop, so resetting
  // the owned setters is sufficient to reset the visible table - no reload needed.
  const resetState = () => {
    localStorage.removeItem(identifier + '_mrt_state');
    setColumnFilters(defaultColumnFilters ?? []);
    setColumnFilterFns(
      buildDefaultColumnFilterFns(columns, defaultColumnFilterFns),
    );
    setColumnVisibility(defaultColumnVisibility ?? {});
    setColumnSizing(defaultColumnSizing ?? {});
    setDensity(defaultDensity ?? 'xs');
    setGlobalFilter(defaultGlobalFilter ?? '');
    setSorting(defaultSorting ?? []);
    setPagination(defaultPagination ?? { pageIndex: 0, pageSize: 10 });
    setShowGlobalFilter(defaultShowGlobalFilter ?? false);
    setShowColumnFilters(defaultShowColumnFilters ?? false);
  };

  return {
    /** False until the saved state has been read; until then every field holds its default. */
    isRestored: !isFirstRender,
    start,
    length,
    filters,
    filterFns,
    columnFilters,
    setColumnFilters,
    columnVisibility,
    setColumnVisibility,
    columnSizing,
    setColumnSizing,
    columnFilterFns,
    setColumnFilterFns,
    globalFilter,
    setGlobalFilter,
    showColumnFilters,
    setShowColumnFilters,
    showGlobalFilter,
    setShowGlobalFilter,
    density,
    setDensity,
    sorting,
    setSorting,
    pagination,
    setPagination,
    resetState,
  };
};
export interface IUseTableProps<T extends MRT_RowData> {
  columnFilters: MRT_ColumnFiltersState;
  setColumnFilters: React.Dispatch<
    React.SetStateAction<MRT_ColumnFiltersState>
  >;
  columnFilterFns: MRT_ColumnFilterFnsState;
  setColumnFilterFns: React.Dispatch<
    React.SetStateAction<MRT_ColumnFilterFnsState>
  >;
  columnVisibility: MRT_VisibilityState;
  setColumnVisibility: React.Dispatch<
    React.SetStateAction<MRT_VisibilityState>
  >;
  columnSizing: MRT_ColumnSizingState;
  setColumnSizing: React.Dispatch<React.SetStateAction<MRT_ColumnSizingState>>;
  showGlobalFilter: boolean;
  setShowGlobalFilter: React.Dispatch<React.SetStateAction<boolean>>;
  showColumnFilters: boolean;
  setShowColumnFilters: React.Dispatch<React.SetStateAction<boolean>>;
  density: MRT_DensityState;
  setDensity: React.Dispatch<React.SetStateAction<MRT_DensityState>>;
  globalFilter: string;
  setGlobalFilter: React.Dispatch<React.SetStateAction<string>>;
  sorting: MRT_SortingState;
  setSorting: React.Dispatch<React.SetStateAction<MRT_SortingState>>;
  pagination: MRT_PaginationState;
  setPagination: React.Dispatch<React.SetStateAction<MRT_PaginationState>>;
  getRowId?: (
    originalRow: T,
    index: number,
    parentRow: MRT_Row<T>,
  ) => string | undefined;
  rowSelection?: MRT_RowSelectionState;
  /** Controls row expansion when set; pair with `tableOptions.onExpandedChange`. */
  expanded?: MRT_ExpandedState;

  // Omits every key the hook itself controls (state, on*Change wiring,
  // rowCount, renderToolbarInternalActions) so a caller passing one of these
  // fails to compile instead of being silently clobbered. `getRowId` stays
  // allowed here and the top-level prop wins when both are supplied.
  tableOptions: Omit<
    MRT_TableOptions<T>,
    | 'state'
    | 'onColumnFilterFnsChange'
    | 'onColumnFiltersChange'
    | 'onColumnVisibilityChange'
    | 'onColumnSizingChange'
    | 'onDensityChange'
    | 'onGlobalFilterChange'
    | 'onShowColumnFiltersChange'
    | 'onShowGlobalFilterChange'
    | 'onPaginationChange'
    | 'onSortingChange'
    | 'rowCount'
    | 'renderToolbarInternalActions'
  >;
  totalCount: number;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error?: string;

  // Extended props to enhance the table.
  resetState: () => void;
  showResetStateButton?: boolean;
  size?: 'normal' | 'compact';

  toolbarExportCsvOptions?: {
    enabled: boolean;
    transform: (
      data: T[],
    ) => { [key: string]: string | number | boolean | null | undefined }[];
    filename?: string;
  };

  /** Callback to refresh table data. When provided, adds a refresh icon to the toolbar. */
  onRefresh?: () => void;
}
/** @registry Wraps mantine-react-table with Firetrol defaults (manual filtering/sorting/pagination, refresh/CSV export toolbar actions, reset-state button); takes the state from useTableState. */
export const useTable = <T extends MRT_RowData>({
  tableOptions,
  totalCount,
  isLoading,
  isFetching,
  isError,
  error,
  columnFilters,
  setColumnFilters,
  columnFilterFns,
  setColumnFilterFns,
  columnVisibility,
  setColumnVisibility,
  columnSizing,
  setColumnSizing,
  globalFilter,
  setGlobalFilter,
  density,
  setDensity,
  showColumnFilters,
  setShowColumnFilters,
  showGlobalFilter,
  setShowGlobalFilter,
  sorting,
  setSorting,
  pagination,
  setPagination,
  getRowId,
  rowSelection,
  expanded,
  resetState,
  showResetStateButton = true,
  size = 'normal',
  toolbarExportCsvOptions,
  onRefresh,
}: IUseTableProps<T>) => {
  const defaultRenderToolbarInternalActions = useCallback(
    (props: { table: MRT_TableInstance<T> }) => {
      const { table } = props;
      return (
        <>
          {onRefresh && (
            <BasicActionIcon
              onClick={onRefresh}
              variant="subtle"
              color="gray"
              loading={isFetching}
              label="Refresh data"
            >
              <IconRefresh size={18} />
            </BasicActionIcon>
          )}
          {toolbarExportCsvOptions?.enabled && (
            <ExportCsvToolbarAction
              table={table}
              transform={toolbarExportCsvOptions.transform}
              filename={toolbarExportCsvOptions.filename}
            />
          )}

          {/* built-in buttons (must pass in table prop for them to work!) */}
          <MRT_ToggleDensePaddingButton table={table} />
          <MRT_ToggleFiltersButton table={table} />
          <MRT_ShowHideColumnsButton table={table} />
          <MRT_ToggleFullScreenButton table={table} />
        </>
      );
    },
    [toolbarExportCsvOptions, onRefresh, isFetching],
  );

  const theme = useMantineTheme();

  const getCompactStyles = () =>
    size === 'compact'
      ? {
          fontSize: theme.fontSizes.xs,
          padding: theme.spacing.xs,
        }
      : {};

  const mergePropsWithCompactStyles = <T extends Record<string, any>>(
    props: T | undefined,
  ) => {
    if (size !== 'compact') return props;

    const existingStyle = (props as any)?.style || {};
    return {
      ...props,
      style: {
        ...existingStyle,
        ...getCompactStyles(),
      },
    } as T;
  };

  const config = useMantineReactTable({
    ...tableOptions,
    enableClickToCopy: tableOptions.enableClickToCopy || false,
    mantineCopyButtonProps: {
      style: { width: '100%' },
    },
    mantineFilterTextInputProps: {
      placeholder: '',
      ...tableOptions.mantineFilterTextInputProps,
    },
    mantinePaperProps: {
      shadow: 0,
      withBorder: true,
      ...tableOptions.mantinePaperProps,
    },
    mantineTableProps: mergePropsWithCompactStyles(
      tableOptions.mantineTableProps,
    ),
    mantineTableHeadCellProps: {
      ...mergePropsWithCompactStyles(tableOptions.mantineTableHeadCellProps),
      className: classes['table-head-cell'],
    },
    mantineTableBodyCellProps: mergePropsWithCompactStyles(
      tableOptions.mantineTableBodyCellProps,
    ),
    mantineTableFooterCellProps: mergePropsWithCompactStyles(
      tableOptions.mantineTableFooterCellProps,
    ),
    displayColumnDefOptions: {
      ...tableOptions.displayColumnDefOptions,
      'mrt-row-actions': {
        size: 100,
        ...tableOptions.displayColumnDefOptions?.['mrt-row-actions'],
      },
    },
    enableRowActions: tableOptions.enableRowActions ?? true,
    enableGlobalFilter: tableOptions.enableGlobalFilter || false,
    enableColumnFilterModes: tableOptions.enableColumnFilterModes ?? true,
    columnFilterModeOptions: tableOptions.columnFilterModeOptions || [
      'contains',
      'startsWith',
      'endsWith',
    ],
    enableColumnResizing: tableOptions.enableColumnResizing ?? true,
    defaultColumn: {
      minSize: 40,
      maxSize: 1000,
      size: 200,
    },
    initialState: tableOptions.initialState || { showColumnFilters: true },
    manualFiltering: tableOptions.manualFiltering ?? true,
    manualPagination: tableOptions.manualPagination ?? true,
    manualSorting: tableOptions.manualSorting ?? true,
    mantineToolbarAlertBannerProps: isError
      ? {
          color: 'red',
          children: error ?? 'Error loading data',
        }
      : undefined,
    onColumnFilterFnsChange: setColumnFilterFns,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnSizingChange: setColumnSizing,
    onDensityChange: setDensity,
    onGlobalFilterChange: setGlobalFilter,
    onShowColumnFiltersChange: setShowColumnFilters,
    onShowGlobalFilterChange: setShowGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    getRowId: getRowId ?? tableOptions.getRowId,
    renderToolbarInternalActions: defaultRenderToolbarInternalActions,
    rowCount: totalCount,
    state: {
      columnFilterFns,
      columnFilters,
      globalFilter,
      columnVisibility,
      columnSizing,
      density,
      isLoading,
      pagination,
      showAlertBanner: isError,
      showProgressBars: isFetching,
      showColumnFilters,
      showGlobalFilter,
      sorting,
      rowSelection: rowSelection ?? {},
      ...(expanded === undefined ? {} : { expanded }),
    },
    renderDetailPanel:
      totalCount > 0 ? tableOptions.renderDetailPanel : undefined,
  } as MRT_TableOptions<T>);

  const table = (
    <>
      <MantineReactTable table={config} />
      {showResetStateButton && (
        <Group justify="end">
          <SecondaryButton
            onClick={resetState}
            size="xs"
            mt="xs"
            variant="subtle"
            color="gray"
          >
            Problem? Click to Reset Table
          </SecondaryButton>
        </Group>
      )}
    </>
  );

  return {
    table,
    config,
  };
};
